import React, { useState, useEffect, useCallback, useContext, useRef } from "react";
import { Button, Input, Modal, ModalHeader, ModalBody } from "reactstrap";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import Header from "../../layout/Header/Header";
import { AuthContext } from "../../contexts/AuthContext";
import { posGet, posPost, posPatch, avisaError, dinero, numero, hoy, sumaDias, fechaHora, esMaster } from "./posApi";
import { llave, variante } from "./posApi";
import VariantePicker from "./VariantePicker";
import { imprimirEtiquetas } from "./etiquetasPdf";
import { useTienda, TiendaSelector } from "./TiendaSelector";
import SinPermiso from "./SinPermiso";
import ModeloModal from "../Articulos/ModeloModal";
import FotoModelo from "../Articulos/FotoModelo";

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

function PosEntradas() {
  const { user } = useContext(AuthContext);
  const master = esMaster(user);
  const puedeCapturar = !!(user && user.posEntradas);
  // Quien no ve costos no los captura: el sistema usa el costo de referencia del modelo
  const verCostos = !!(user && user.posCostos);
  // El catálogo de modelos es de oficina: solo usuarios master con permiso de crear modelos
  const puedeCrearModelos = !!(master && user && user.articulos_create);
  const ctx = useTienda(false);
  const { tienda, nombreTienda } = ctx;

  const [renglones, setRenglones] = useState([]); // {articulos, codigo, nombre, cantidad, costo}
  const [codigo, setCodigo] = useState("");
  // Sugerencias mientras escribe (como en la caja)
  const [sugerencias, setSugerencias] = useState([]);
  const [sinCoincidencias, setSinCoincidencias] = useState(false);
  const [marcado, setMarcado] = useState(-1);
  const consulta = useRef(0);
  const [nuevoModelo, setNuevoModelo] = useState(null); // {} abre el alta de modelo
  const [observaciones, setObservaciones] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [inicio, setInicio] = useState(sumaDias(hoy(), -30));
  const [fin, setFin] = useState(hoy());
  const [detalle, setDetalle] = useState(null);
  const [picker, setPicker] = useState(null);
  const inputArchivo = useRef(null);

  const cargar = useCallback(() => {
    if (!tienda) return;
    posGet("/entradas", { area: tienda, inicio, fin }).then(setHistorial).catch(avisaError);
  }, [tienda, inicio, fin]);
  useEffect(() => { cargar(); }, [cargar]);

  // Agrega renglones por variante; si ya estaban, suma cantidad
  const agregarFilas = (filas) => {
    setRenglones((prev) => {
      const out = [...prev];
      filas.forEach((f) => {
        const k = llave(f.articulos, f.colores, f.tallas);
        const i = out.findIndex((r) => r.k === k);
        if (i >= 0) out[i] = { ...out[i], cantidad: Number(out[i].cantidad) + Number(f.cantidad), costo: f.costo };
        else out.push({ ...f, k });
      });
      return out;
    });
  };
  const deModelo = (a, v) => ({ articulos: a._id, codigo: a.codigo, nombre: a.nombre, colores: v.colores, tallas: v.tallas, color: v.color, talla: v.talla, cantidad: v.cantidad, costo: v.costo });

  useEffect(() => {
    const q = codigo.trim();
    if (q.length < 2 || !tienda) { setSugerencias([]); setSinCoincidencias(false); setMarcado(-1); return undefined; }
    const id = ++consulta.current;
    const t = setTimeout(async () => {
      try {
        const r = await posGet("/articulos", { area: tienda, q });
        if (id !== consulta.current) return;
        setSugerencias(r); setSinCoincidencias(!r.length); setMarcado(-1);
      } catch (er) { /* al dar Enter se muestra el error */ }
    }, 250);
    return () => clearTimeout(t);
  }, [codigo, tienda]);
  const cerrarSugerencias = () => { consulta.current++; setSugerencias([]); setSinCoincidencias(false); setMarcado(-1); };

  const agregarCodigo = async (e, elegido) => {
    e.preventDefault();
    // Clic en una sugerencia, o Enter con una elegida con flechas: se agrega esa
    const c = (elegido || (marcado >= 0 && sugerencias[marcado] ? sugerencias[marcado].codigo : codigo)).trim();
    if (!c) return;
    cerrarSugerencias();
    try {
      const r = await posPost("/codigos", { area: tienda, codigos: [c] });
      if (!r.encontrados.length) return Swal.fire({ icon: "info", title: "No existe", text: `No hay un modelo con código ${c}`, timer: 1800, showConfirmButton: false });
      const a = r.encontrados[0];
      setCodigo("");
      if ("tallaSel" in a) { // escanearon la etiqueta de una talla y color
        agregarFilas([deModelo(a, { colores: a.colorSel, tallas: a.tallaSel, color: a.color, talla: a.talla, cantidad: 1, costo: verCostos ? a.costoMX || 0 : "" })]);
        return;
      }
      const conVariantes = a.variantes.length > 1 || (a.variantes[0] && (a.variantes[0].colores || a.variantes[0].tallas));
      if (conVariantes) setPicker(a);
      else agregarFilas([deModelo(a, { colores: null, tallas: null, cantidad: 1, costo: verCostos ? a.costoMX || 0 : "" })]);
    } catch (er) { avisaError(er); }
  };

  const plantilla = () => {
    // "tienda" (clave) es opcional: con ella un solo archivo carga varias tiendas; "costo" vacío = costo de referencia
    const ws = XLSX.utils.aoa_to_sheet(master
      ? [["tienda", "codigo", "color", "talla", "cantidad", "costo"], ["T01", "NF-BL-001", "Negro", "M", 10, 150.5], ["T01", "NF-BL-001", "Negro", "G", 8, ""], ["T02", "NF-AC-001", "", "", 5, 90]]
      : [["codigo", "color", "talla", "cantidad", "costo"], ["NF-BL-001", "Negro", "M", 10, 150.5], ["NF-BL-001", "Negro", "G", 8, ""], ["NF-AC-001", "", "", 5, 90]]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Entrada");
    XLSX.writeFile(wb, "plantilla_entrada_inventario.xlsx");
  };

  const costoValido = (c) => c === "" || c === undefined || (!isNaN(Number(c)) && Number(c) >= 0);

  const leerExcel = (e) => {
    const archivo = e.target.files[0];
    e.target.value = "";
    if (!archivo) return;
    const lector = new FileReader();
    lector.onload = async (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: "array" });
        const filas = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: "" });
        const norm = filas.map((f) => {
          const k = {};
          Object.keys(f).forEach((x) => { k[x.toString().trim().toLowerCase()] = f[x]; });
          return {
            tienda: String(k.tienda || "").trim().toUpperCase(),
            codigo: String(k.codigo || k["código"] || "").trim(), color: String(k.color || "").trim(), talla: String(k.talla || "").trim(),
            cantidad: Number(k.cantidad), costo: verCostos && String(k.costo).trim() !== "" ? Number(k.costo) : "",
          };
        });
        if (!norm.some((f) => f.codigo)) return Swal.fire({ icon: "warning", title: "Archivo vacío", text: "Usa las columnas: codigo, color, talla, cantidad, costo" });
        const varias = norm.some((f) => f.tienda);
        if (varias && !master) return Swal.fire({ icon: "warning", title: "Columna tienda", text: "Cargar a varias tiendas requiere acceso a todas las tiendas. Quita la columna tienda." });
        const r = await posPost("/codigos", { area: tienda, filas: norm });
        const errores = [...r.errores];
        const buenas = r.encontrados.filter((f) => {
          const orig = norm[f.fila - 2] || {};
          if (!(Number(f.cantidad) > 0) || !costoValido(orig.costo)) { errores.push({ fila: f.fila, codigo: f.codigo, motivo: "Cantidad o costo inválido" }); return false; }
          return true;
        }).map((f) => ({ ...f, costo: (norm[f.fila - 2] || {}).costo, tiendaClave: (norm[f.fila - 2] || {}).tienda }));
        const lista = (errs) => errs.slice(0, 25).map((x) => `Fila ${x.fila} · ${x.codigo}: ${x.motivo}`).join("<br/>") + (errs.length > 25 ? "<br/>…" : "");

        if (!varias) {
          agregarFilas(buenas.map((f) => deModelo(f, { colores: f.colorSel, tallas: f.tallaSel, color: f.color, talla: f.talla, cantidad: Number(f.cantidad), costo: f.costo })));
          return Swal.fire({
            icon: errores.length ? "warning" : "success", title: `${buenas.length} renglón(es) cargados`,
            html: errores.length ? `<div style="text-align:left;font-size:0.85rem"><b>${errores.length} con problema (no se cargaron):</b><br/>${lista(errores)}</div>` : "",
          });
        }

        // Varias tiendas: una entrada por tienda, todo o nada por tienda
        const tiendas = await posGet("/tiendas", { destinos: "Si" });
        const porClave = Object.fromEntries(tiendas.map((t) => [String(t.clave || "").toUpperCase(), t]));
        const grupos = {};
        buenas.forEach((f) => {
          const t = porClave[f.tiendaClave];
          if (!t) { errores.push({ fila: f.fila, codigo: f.codigo, motivo: `Tienda "${f.tiendaClave || "vacía"}" no existe o está inactiva` }); return; }
          (grupos[t._id] = grupos[t._id] || { t, renglones: [] }).renglones.push(f);
        });
        if (errores.length) {
          return Swal.fire({ icon: "warning", title: "Corrige el archivo", html: `<div style="text-align:left;font-size:0.85rem"><b>${errores.length} con problema; no se cargó nada:</b><br/>${lista(errores)}</div>` });
        }
        const resumen = Object.values(grupos).map((g) => `<tr><td>${g.t.clave} · ${g.t.name}</td><td style="text-align:right">${numero(g.renglones.reduce((x, f) => x + Number(f.cantidad), 0))} pzs</td></tr>`).join("");
        const ok = await Swal.fire({
          title: `¿Dar entrada a ${Object.keys(grupos).length} tienda(s)?`, icon: "question", showCancelButton: true, confirmButtonText: "Sí, dar entrada", cancelButtonText: "Cancelar",
          html: `<table style="width:100%;font-size:0.85rem">${resumen}</table><div class="small text-muted mt-2">Se registra una entrada por tienda.</div>`,
        });
        if (!ok.value) return;
        const hechas = [], fallas = [];
        for (const g of Object.values(grupos)) {
          try {
            const en = await posPost("/entradas", {
              area: g.t._id, observaciones: observaciones || `Carga por Excel: ${archivo.name}`,
              renglones: g.renglones.map((f) => ({ articulos: f._id, colores: f.colorSel, tallas: f.tallaSel, cantidad: Number(f.cantidad), ...(f.costo !== "" ? { costo: Number(f.costo) } : {}) })),
            });
            hechas.push(`${g.t.clave}: entrada ${en.folio}`);
          } catch (er) { fallas.push(`${g.t.clave}: ${(er.response && er.response.data && er.response.data.message) || "error"}`); }
        }
        Swal.fire({
          icon: fallas.length ? "warning" : "success", title: `${hechas.length} entrada(s) registradas`,
          html: `<div style="text-align:left;font-size:0.85rem">${hechas.join("<br/>")}${fallas.length ? `<br/><b>No se registraron:</b><br/>${fallas.join("<br/>")}` : ""}<br/><br/>Las etiquetas se imprimen desde Etiquetas → Por entrada.</div>`,
        });
        cargar();
      } catch (er) { avisaError(er); }
    };
    lector.readAsArrayBuffer(archivo);
  };

  const cambia = (i, campo, valor) => setRenglones((prev) => prev.map((r, j) => (j === i ? { ...r, [campo]: valor } : r)));
  const totalPiezas = renglones.reduce((t, r) => t + (Number(r.cantidad) || 0), 0);
  const totalImporte = r2(renglones.reduce((t, r) => t + (Number(r.cantidad) || 0) * (Number(r.costo) || 0), 0));

  const guardar = async () => {
    if (!renglones.length) return;
    if (renglones.some((r) => !(Number(r.cantidad) > 0) || !costoValido(r.costo))) {
      return Swal.fire({ icon: "warning", title: verCostos ? "Revisa cantidades y costos" : "Revisa las cantidades" });
    }
    const ok = await Swal.fire({
      title: "¿Dar entrada al inventario?",
      html: `<b>${numero(totalPiezas)}</b> piezas${verCostos ? ` por <b>${dinero(totalImporte)}</b>` : ""} a <b>${nombreTienda}</b>`,
      icon: "question", showCancelButton: true, confirmButtonText: "Sí, dar entrada", cancelButtonText: "Revisar",
    });
    if (!ok.value) return;
    setGuardando(true);
    try {
      const e = await posPost("/entradas", {
        area: tienda, observaciones,
        renglones: renglones.map((r) => ({ articulos: r.articulos, colores: r.colores, tallas: r.tallas, cantidad: Number(r.cantidad), ...(verCostos && r.costo !== "" ? { costo: Number(r.costo) } : {}) })),
      });
      const r = await Swal.fire({
        icon: "success", title: `Entrada ${e.folio} registrada`, text: "¿Imprimes las etiquetas de estas piezas?",
        showCancelButton: true, confirmButtonText: "Sí, imprimir etiquetas", cancelButtonText: "Después",
      });
      if (r.value) etiquetasDe(e._id);
      setRenglones([]); setObservaciones(""); cargar();
    } catch (er) { avisaError(er); } finally { setGuardando(false); }
  };

  const etiquetasDe = async (id) => {
    try {
      let formato = "rollo";
      try { formato = localStorage.getItem("pos-etiquetas-formato") || "rollo"; } catch (er) { /* sin storage */ }
      imprimirEtiquetas(await posGet(`/etiquetas/entrada/${id}`, { area: tienda }), { formato });
    } catch (er) { avisaError(er); }
  };

  const cancelar = async (e) => {
    const r = await Swal.fire({ title: `¿Cancelar la entrada ${e.folio}?`, text: "Se descuenta la mercancía de la tienda.", icon: "warning", showCancelButton: true, confirmButtonText: "Sí, cancelar", cancelButtonText: "No" });
    if (!r.value) return;
    try { await posPatch(`/entradas/${e._id}/cancelar`, { area: tienda }); setDetalle(null); cargar(); } catch (er) { avisaError(er); }
  };

  if (!(user && user.rolTiendas && (user.posEntradas || user.posCancelarEntradas))) return <SinPermiso que="dar entradas de inventario" />;

  return (
    <>
      <Header />
      <div className="card container col-12" style={{ padding: "18px 22px" }}>
        <div className="page-header">
          <div>
            <h3 className="page-title"><i className="fas fa-dolly mr-2" style={{ color: "#8b5cf6" }} />Entradas de inventario</h3>
            <p className="page-subtitle">Alta de mercancía a una tienda (no genera compra ni cuenta por pagar){verCostos ? "; costo vacío = costo de referencia del modelo" : ""}</p>
          </div>
          <div className="page-actions">
            <TiendaSelector ctx={ctx} />
            <Button color="secondary" size="sm" href="/PuntoVenta"><i className="fas fa-th-large mr-1" />Menú</Button>
          </div>
        </div>

        {puedeCapturar && tienda && (
          <div className="form-section">
            <div className="form-section-header"><i className="fas fa-plus" /><h4 className="form-section-title">Nueva entrada a {nombreTienda}</h4></div>
            <div className="form-section-body">
              <div className="filter-bar">
                <form onSubmit={agregarCodigo} style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                  <div style={{ position: "relative" }}>
                    <label>Código, código de barras o nombre</label>
                    <Input value={codigo} onChange={(e) => setCodigo(e.target.value)} placeholder="Escribe y elige, o escanea y Enter" style={{ minWidth: 320 }} autoComplete="off"
                      onKeyDown={(e) => {
                        if (e.key === "Escape") { cerrarSugerencias(); setCodigo(""); }
                        else if (e.key === "ArrowDown" && sugerencias.length) { e.preventDefault(); setMarcado((m) => Math.min(m + 1, sugerencias.length - 1)); }
                        else if (e.key === "ArrowUp" && sugerencias.length) { e.preventDefault(); setMarcado((m) => Math.max(m - 1, -1)); }
                      }}
                      onBlur={() => setTimeout(cerrarSugerencias, 200)} />
                    {(sugerencias.length > 0 || (sinCoincidencias && codigo.trim().length >= 2)) && (
                      <div style={{ position: "absolute", zIndex: 30, left: 0, top: "100%", marginTop: 4, width: 520, maxWidth: "80vw", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, boxShadow: "0 10px 30px rgba(0,0,0,.12)", maxHeight: 340, overflowY: "auto" }}>
                        {!sugerencias.length && (
                          <div style={{ padding: "10px 14px", color: "#64748b", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            Sin coincidencias para "{codigo.trim()}"
                            {puedeCrearModelos && <Button size="sm" color="success" outline onMouseDown={(e) => e.preventDefault()} onClick={() => setNuevoModelo({})}><i className="fas fa-plus mr-1" />Darlo de alta</Button>}
                          </div>
                        )}
                        {sugerencias.map((a, i) => (
                          <button key={a._id} type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onMouseEnter={() => setMarcado(i)}
                            onClick={() => agregarCodigo({ preventDefault: () => {} }, a.codigo)}
                            style={{ display: "flex", width: "100%", justifyContent: "space-between", alignItems: "center", padding: "9px 14px", border: "none", borderBottom: "1px solid #f1f5f9", background: marcado === i ? "#eff6ff" : "transparent", textAlign: "left", cursor: "pointer" }}>
                            <span style={{ display: "flex", alignItems: "center", gap: 10 }}><FotoModelo url={a.urlFoto} size={32} ampliar={false} /><span><strong>{a.codigo}</strong> · {a.nombre}</span></span>
                            <span className={`badge badge-${a.existencia > 0 ? "success" : "secondary"}`} title="Existencia en esta tienda">{numero(a.existencia)} pzs</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button color="primary" type="submit"><i className="fas fa-plus mr-1" />Agregar</Button>
                  {puedeCrearModelos && (
                    <Button color="secondary" outline type="button" onClick={() => setNuevoModelo({})} title="Dar de alta un modelo que todavía no existe">
                      <i className="fas fa-tshirt mr-1" />Nuevo modelo
                    </Button>
                  )}
                </form>
                <div style={{ flexDirection: "row", gap: 8 }}>
                  <input ref={inputArchivo} type="file" accept=".xlsx,.xls,.csv" style={{ display: "none" }} onChange={leerExcel} />
                  <Button color="success" onClick={() => inputArchivo.current.click()}><i className="fas fa-file-excel mr-1" />Cargar Excel</Button>{" "}
                  <Button color="secondary" outline onClick={plantilla}><i className="fas fa-download mr-1" />Descargar plantilla</Button>
                </div>
              </div>

              <div className="table-responsive">
                <table className="table table-sm">
                  <thead><tr><th>Código</th><th>Modelo</th><th>Color · Talla</th><th style={{ width: 120 }}>Cantidad</th>{verCostos && <><th style={{ width: 140 }}>Costo unitario</th><th className="text-right">Importe</th></>}<th /></tr></thead>
                  <tbody>
                    {!renglones.length && <tr><td colSpan={7} className="text-center text-muted py-3">Agrega artículos por código o carga un Excel</td></tr>}
                    {renglones.map((r, i) => (
                      <tr key={r.k}>
                        <td><strong>{r.codigo}</strong></td>
                        <td>{r.nombre}</td>
                        <td>{variante(r) || "—"}</td>
                        <td><Input bsSize="sm" type="number" min="1" value={r.cantidad} onChange={(e) => cambia(i, "cantidad", e.target.value)} /></td>
                        {verCostos && <>
                          <td><Input bsSize="sm" type="number" min="0" step="0.01" value={r.costo} placeholder="De referencia" onChange={(e) => cambia(i, "costo", e.target.value)} /></td>
                          <td className="text-right">{r.costo === "" ? "—" : dinero((Number(r.cantidad) || 0) * (Number(r.costo) || 0))}</td>
                        </>}
                        <td><Button size="sm" color="danger" onClick={() => setRenglones(renglones.filter((_, j) => j !== i))}><i className="fas fa-times" /></Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Input className="mb-2" placeholder="Observaciones (ej. inventario inicial, reposición…)" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
              <div className="total-bar">
                Piezas <span className="badge badge-secondary">{numero(totalPiezas)}</span>
                {verCostos && <>Total <span className="badge badge-success">{dinero(totalImporte)}</span></>}
              </div>
              <div className="submit-bar">
                {renglones.length > 0 && <Button color="secondary" onClick={() => setRenglones([])}>Limpiar</Button>}
                <Button color="success" disabled={!renglones.length || guardando} onClick={guardar}>
                  {guardando ? <i className="fas fa-spinner fa-spin" /> : <><i className="fas fa-save mr-1" />Dar entrada</>}
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="form-section">
          <div className="form-section-header dark"><i className="fas fa-history" /><h4 className="form-section-title">Entradas registradas</h4></div>
          <div className="form-section-body">
            <div className="filter-bar">
              <div><label>Desde</label><Input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} /></div>
              <div><label>Hasta</label><Input type="date" value={fin} onChange={(e) => setFin(e.target.value)} /></div>
            </div>
            <div className="table-responsive">
              <table className="table table-sm">
                <thead><tr><th>Folio</th><th>Fecha</th><th>Capturó</th><th>Observaciones</th><th className="text-right">Piezas</th>{verCostos && <th className="text-right">Total</th>}<th>Estatus</th><th /></tr></thead>
                <tbody>
                  {!historial.length && <tr><td colSpan={8} className="text-center text-muted">Sin entradas en el periodo</td></tr>}
                  {historial.map((e) => (
                    <tr key={e._id} style={e.estatus === "Cancelada" ? { color: "#94a3b8" } : {}}>
                      <td>{e.folio}</td><td>{fechaHora(e.fecha)}</td><td>{e.user ? e.user.nombre : ""}</td><td>{e.observaciones}</td>
                      <td className="text-right">{numero(e.piezas)}</td>{verCostos && <td className="text-right">{dinero(e.total)}</td>}
                      <td>{e.estatus === "Cancelada" ? <span className="badge badge-danger">Cancelada</span> : <span className="badge badge-success">Activa</span>}</td>
                      <td><Button size="sm" color="info" onClick={() => setDetalle(e)}><i className="fas fa-eye" /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <ModeloModal
        modelo={nuevoModelo}
        inicial={(() => { const t = codigo.trim(); if (!t) return {}; return /\s/.test(t) ? { nombre: t } : { codigo: t.toUpperCase() }; })()}
        onCerrar={() => setNuevoModelo(null)}
        onGuardado={(m) => { setNuevoModelo(null); setCodigo(""); agregarCodigo({ preventDefault: () => {} }, m.codigo); }}
      />

      <VariantePicker
        articulo={picker}
        modo="entrada"
        conCosto={verCostos}
        onAgregar={(lista) => { agregarFilas(lista.map((v) => deModelo(picker, v))); setPicker(null); }}
        onCerrar={() => setPicker(null)}
      />

      <Modal isOpen={!!detalle} toggle={() => setDetalle(null)} size="lg">
        <ModalHeader toggle={() => setDetalle(null)}>Entrada {detalle && detalle.folio}</ModalHeader>
        <ModalBody>
          {detalle && (
            <>
              <table className="table table-sm">
                <thead><tr><th>Código</th><th>Modelo</th><th>Color · Talla</th><th className="text-right">Cantidad</th>{verCostos && <><th className="text-right">Costo</th><th className="text-right">Importe</th></>}</tr></thead>
                <tbody>
                  {detalle.renglones.map((r) => (
                    <tr key={llave(r.articulos, r.colores, r.tallas)}><td>{r.codigo}</td><td>{r.nombre}</td><td>{variante(r) || "—"}</td><td className="text-right">{numero(r.cantidad)}</td>{verCostos && <><td className="text-right">{dinero(r.costo)}</td><td className="text-right">{dinero(r.importe)}</td></>}</tr>
                  ))}
                </tbody>
              </table>
              {detalle.estatus === "Activa" && (
                <Button color="dark" className="mr-2" onClick={() => etiquetasDe(detalle._id)}><i className="fas fa-barcode mr-1" />Imprimir etiquetas</Button>
              )}
              {user.posCancelarEntradas && detalle.estatus === "Activa" && (
                <div className="text-right"><Button color="danger" onClick={() => cancelar(detalle)}><i className="fas fa-ban mr-1" />Cancelar entrada</Button></div>
              )}
            </>
          )}
        </ModalBody>
      </Modal>
    </>
  );
}

export default PosEntradas;
